// ======================================================================
// File: src/modules/ppe/transport/components/transport/TransportList.jsx
// Module: PPE → Transport
// Purpose:
//   Main operational list screen for Transport Requests
//   Pattern aligned with PPE module UI standard
// ======================================================================

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import transportService from "../../services/transportService";
import TransportDrawer from "./TransportDrawer";
import TransportStatusBadge from "../../shared/TransportStatusBadge";
import TransportKpiBar from "./TransportKpiBar";
import TransportHeaderBanner from "../layout/TransportHeaderBanner";
import TransportFilterBar from "./TransportFilterBar";


// ======================================================================
// COMPONENT
// ======================================================================

export default function TransportList() {

  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [transports, setTransports] = useState([]);
  const [editingTransport, setEditingTransport] = useState(null);
  

  // ✅ safe KPI defaults
  const [summary, setSummary] = useState({
    active: 0,
    in_transit: 0,
    delivered: 0,
    payment_pending: 0,
  });

  const [showDrawer, setShowDrawer] = useState(false);

  // ------------------------------------------------------------------
  // KPI CALCULATOR (reusable)
  // ------------------------------------------------------------------
  const updateSummary = (data) => {
    setSummary({
      active: data.length,
      in_transit: data.filter(
        (t) => t.status_code === "IN_TRANSIT"
      ).length,
      delivered: data.filter(
        (t) => t.status_code === "DELIVERED"
      ).length,
      payment_pending: data.filter(
        (t) => t.overall_payment_status !== "PAID"
      ).length,
    });
  };

  // ------------------------------------------------------------------
  // LOAD DATA
  // ------------------------------------------------------------------
  const loadTransports = async () => {
    try {
      setLoading(true);

      const res = await transportService.getTransportList();
      const data = res?.data || [];

      setTransports(data);
      updateSummary(data);

    } catch (err) {
      console.error("Failed to load transports", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTransports();
  }, []);

  // ------------------------------------------------------------------
  // FILTER SEARCH
  // ------------------------------------------------------------------
  const handleSearch = async (filters) => {
    try {
      setLoading(true);

      const res = await transportService.getTransportList(filters);
      const data = res?.data || [];

      setTransports(data);
      updateSummary(data);

    } catch (err) {
      console.error("Search failed", err);
    } finally {
      setLoading(false);
    }
  };

  // ------------------------------------------------------------------
  // NAVIGATION
  // ------------------------------------------------------------------
  const openDetail = (id) => {
    navigate(`${id}`);
  };

  // ------------------------------------------------------------------
  // DRAWER HANDLERS
  // ------------------------------------------------------------------
  const handleCreated = () => {
    setShowDrawer(false);
    setEditingTransport(null);
    loadTransports();
  };

  // ------------------------------------------------------------------
  // RENDER
  // ------------------------------------------------------------------
  return (
    <div className="transport-list-page">

      {/* ============================================================
          HEADER BANNER
      ============================================================ */}
      <TransportHeaderBanner
        onCreate={() => {
          console.log("CREATE CLICKED");
          setEditingTransport(null);
          setShowDrawer(true);
        }}
      />


      {/* ============================================================
          FILTER BAR (NEW)
      ============================================================ */}
      <TransportFilterBar onSearch={handleSearch} />

      {/* ============================================================
          KPI BAR
      ============================================================ */}
      <TransportKpiBar summary={summary} />

      {/* ============================================================
          TABLE
      ============================================================ */}
      <div className="card glass-card">
        <div className="card-body p-0">
          <div style={{ width: "100%", overflowX: "auto" }}>

          {loading ? (
            <div className="p-4 text-center">Loading...</div>
          ) : (
            <table className="table table-hover mb-0">
              
            <thead>
              <tr>
                <th>Transport Code</th>
                <th>PO No.</th>
                <th>Type</th>
                <th>Source</th>
                <th>Destination</th>
                <th>Transporter</th>
                <th>Expected Dispatch</th>
                <th>Expected Delivery</th>
                <th>Created Date</th>
                <th>Status</th>
                <th>Vehicles</th>
                <th>Payable Amount</th>
                <th>Paid Amount</th>
                <th>Outstanding</th>
                <th>Pay Status</th>
                <th>Actions</th>
              </tr>
            </thead>


                              
                <tbody>
                  {transports.length === 0 && (
                    <tr>
                      <td colSpan="16" className="text-center p-4">
                        No transport records found
                      </td>
                    </tr>
                  )}

                  {transports.map((t) => {
                    const canEdit = ["REQUESTED", "BOOKED"].includes(
                      t.status_code
                    );

                    const formatDate = (value) => {
                      if (!value) return "-";

                      const date = new Date(value);

                      if (Number.isNaN(date.getTime())) return "-";

                      return date.toLocaleString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      });
                    };

                    const formatAmount = (value) =>
                      Number(value || 0).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      });

                    return (
                      <tr
                        key={t.id}
                        style={{ cursor: "pointer" }}
                        onDoubleClick={() => openDetail(t.id)}
                      >
                        <td>{t.transport_code || "-"}</td>
                        <td>{t.po_no || "-"}</td>
                        <td>{t.request_type || "-"}</td>
                        <td>{t.source_name || "-"}</td>
                        <td>{t.destination_name || "-"}</td>
                        <td>
                          <div>{t.transporter_name_snapshot || "-"}</div>
                          {t.transporter_contact_snapshot && (
                            <small className="text-muted">
                              {t.transporter_contact_snapshot}
                            </small>
                          )}
                        </td>
                        <td>{formatDate(t.expected_dispatch_datetime)}</td>
                        <td>{formatDate(t.expected_delivery_datetime)}</td>
                        <td>{formatDate(t.created_at)}</td>
                        <td>
                          <TransportStatusBadge status={t.status_code} />
                        </td>
                        <td>{t.total_vehicles ?? 0}</td>
                        <td className="text-end">
                          {formatAmount(t.total_payable_amount)}
                        </td>
                        <td className="text-end">
                          {formatAmount(t.total_paid_amount)}
                        </td>
                        <td className="text-end">
                          {formatAmount(t.outstanding_amount)}
                        </td>
                        <td>
                          <span className="badge bg-secondary">
                            {t.overall_payment_status || "PENDING"}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDetail(t.id);
                              }}
                            >
                              View
                            </button>

                            {canEdit && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingTransport(t);
                                    setShowDrawer(true);
                                  }}
                              >
                                Edit
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>

            </table>
          
          )}
          </div>
        </div>
      </div>

      {/* ============================================================
          DRAWER
      ============================================================ */}
      <TransportDrawer
        show={showDrawer}
        
          onClose={() => {
            setShowDrawer(false);
            setEditingTransport(null);
          }}

        onCreated={handleCreated}
        editTransport={editingTransport}
      />


    </div>
  );
}
